/**
 * The outbox (brief §14a.3).
 *
 * Every mutating action is written here **first**, applied optimistically to what is on
 * screen, and only then sent. That order is the whole feature: the thumb is faster than a
 * round trip even on a good connection, and in the cellar bar where the reporting actually
 * happens there is no round trip to be faster than.
 *
 * The rule this file exists to keep (brief §14a.1): **being offline is a state, never an
 * error.** No dialogue, no red bar. A quiet chip, a count of what is waiting, and an error
 * list for the one class of failure that is genuinely the user's to see.
 */

import { ApiError, OfflineError, request } from './api.ts';
import type { Op, OpKind } from './store.ts';
import { outboxStore } from './store.ts';

/** Brief §14a.3: five attempts, then out of the queue and into the error list. */
export const MAX_ATTEMPTS = 5;
const BACKOFF_MIN_MS = 5_000;
const BACKOFF_MAX_MS = 5 * 60_000;
/** Brief §14a.7: at most a hundred per call, so the client chunks. */
const CHUNK = 100;

type Disposition = 'applied' | 'dropped' | 'rejected';
type OpResult = { opId: string; disposition: Disposition; data?: unknown; reason?: string };

export type OutboxStatus =
  | { kind: 'idle' }
  | { kind: 'waiting'; pending: number }
  | { kind: 'syncing'; pending: number }
  | { kind: 'done'; applied: number; coins: number }
  | { kind: 'failed'; failed: number };

/** What an applied op earned (amendment 21): a report's coins, nothing for any other kind. */
function coinsOf(data: unknown): number {
  const coins = (data as { coins?: unknown } | undefined)?.coins;
  return typeof coins === 'number' ? coins : 0;
}

/** Deterministic-ish backoff with jitter, so a fleet of phones does not retry in lockstep. */
function backoffFor(attempts: number): number {
  const base = Math.min(BACKOFF_MAX_MS, BACKOFF_MIN_MS * 2 ** Math.max(0, attempts - 1));
  return base * (0.75 + Math.random() * 0.5);
}

class Outbox {
  pending = $state<Op[]>([]);
  /** Ops that failed for a reason the user can act on. Shown once, retryable by hand. */
  failed = $state<Op[]>([]);
  online = $state(true);
  status = $state<OutboxStatus>({ kind: 'idle' });
  /** Set when a flush stopped because the session expired (brief §14a.5). */
  needsSignIn = $state(false);

  #flushing = false;
  /** Set when a flush is asked for while one is already running; see `flush`. */
  #again = false;
  #timer: ReturnType<typeof setTimeout> | undefined;

  async start(): Promise<void> {
    this.pending = await outboxStore.all();
    if (typeof window === 'undefined') return;

    this.online = navigator.onLine;
    window.addEventListener('online', () => {
      this.online = true;
      void this.flush();
    });
    window.addEventListener('offline', () => {
      this.online = false;
      this.#refreshStatus();
    });
    // Coming back to the foreground is the other reliable moment: a phone that regained
    // signal in a pocket fires no `online` event anybody can rely on.
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') void this.flush();
    });

    this.#refreshStatus();
    void this.flush();
  }

  /**
   * Queue an operation and try to send it.
   *
   * Returns as soon as it is durably queued — deliberately **not** when the server has it.
   * The caller applies its own optimistic update and moves on.
   */
  async enqueue(kind: OpKind, payload: unknown): Promise<Op> {
    const op: Op = {
      opId: crypto.randomUUID(),
      kind,
      // `$state.snapshot`, and it is not a nicety. Every caller here hands over reactive
      // state — the ratings map from the report sheet, the settings object — and reactive
      // state in Svelte 5 is a **Proxy**. The structured clone algorithm refuses a Proxy
      // outright, so `IDBObjectStore.put` threw
      //
      //   DataCloneError: Failed to execute 'put' on 'IDBObjectStore': #<Object> could not
      //   be cloned.
      //
      // and reporting a find failed with a message about `put`, which names the mechanism and
      // not one thing the user did. Snapshotting here rather than at each call site is
      // deliberate: this is the line where data stops being reactive and starts being stored,
      // and a rule that has to be remembered four times is a rule that will be forgotten once.
      payload: $state.snapshot(payload),
      queuedAt: Date.now(),
      attempts: 0,
    };
    await outboxStore.add(op);
    this.pending = [...this.pending, op];
    this.#refreshStatus();
    void this.flush();
    return op;
  }

  async retryFailed(): Promise<void> {
    for (const op of this.failed) {
      await outboxStore.add({ ...op, attempts: 0, lastError: undefined });
    }
    this.failed = [];
    this.pending = await outboxStore.all();
    await this.flush();
  }

  dismissFailed(): void {
    this.failed = [];
    this.#refreshStatus();
  }

  /**
   * Send everything waiting, oldest first.
   *
   * One op failing does not stop the queue: after five attempts it moves to the error list
   * and the rest carries on (brief §14a.3). Without that, one permanently bad op blocks every
   * report made after it, for ever.
   */
  async flush(): Promise<void> {
    if (this.#flushing) {
      // An op queued while a flush is in flight used to be stranded. The loop below advances
      // by CHUNK while `#done` shrinks the list underneath it, so an op appended mid-flight
      // lands behind the cursor and that flush finishes without it — and this call, finding
      // one running, returned at once. It then sat there until the next `online` event or the
      // next report. Found through the swipe deck: a second card swiped within one round trip
      // of the first stayed queued, and its Schwarmgefühl never arrived.
      this.#again = true;
      return;
    }
    if (this.pending.length === 0) return;
    this.#flushing = true;
    this.status = { kind: 'syncing', pending: this.pending.length };

    try {
      let applied = 0;
      let coins = 0;
      for (let start = 0; start < this.pending.length; start += CHUNK) {
        const batch = this.pending.slice(start, start + CHUNK);
        const results = await this.#send(batch);
        if (results === undefined) return; // offline or unauthenticated; the queue stays.

        for (const op of batch) {
          const result = results.find((entry) => entry.opId === op.opId);
          if (result === undefined) {
            await this.#failOnce(op, 'keine Antwort');
            continue;
          }
          // `dropped` is discarded as silently as `applied` is: it is the passage of time,
          // not a mistake anybody made (brief §14a.5).
          if (result.disposition === 'rejected') await this.#reject(op, result.reason);
          else {
            if (result.disposition === 'applied') {
              applied += 1;
              coins += coinsOf(result.data);
            }
            await this.#done(op);
          }
        }
      }

      if (applied > 0) {
        this.status = { kind: 'done', applied, coins };
        // Brief §14a.8: "3 übernommen" for three seconds, then gone.
        setTimeout(() => this.#refreshStatus(), 3000);
      } else {
        this.#refreshStatus();
      }
    } finally {
      this.#flushing = false;
      if (this.#again) {
        this.#again = false;
        void this.flush();
      }
    }
  }

  async #send(batch: Op[]): Promise<OpResult[] | undefined> {
    try {
      const response = await request<{ results: OpResult[] }>('/api/sync', {
        method: 'POST',
        body: JSON.stringify({
          // The server corrects every queued timestamp by the difference (brief §14a.4).
          clientNow: Date.now(),
          ops: batch.map((op) => ({
            opId: op.opId,
            kind: op.kind,
            queuedAt: op.queuedAt,
            payload: op.payload,
          })),
        }),
      });
      this.needsSignIn = false;
      return response.results;
    } catch (cause) {
      if (cause instanceof OfflineError) {
        this.online = false;
        this.#schedule();
        this.#refreshStatus();
        return undefined;
      }
      if (cause instanceof ApiError && cause.status === 401) {
        // **Never clear the queue on a 401.** The session expired; the reports did not stop
        // being real (brief §14a.5). Ask for a sign-in and keep everything.
        this.needsSignIn = true;
        this.#refreshStatus();
        return undefined;
      }
      if (cause instanceof ApiError && cause.status === 429) {
        this.#schedule();
        this.#refreshStatus();
        return undefined;
      }
      for (const op of batch) await this.#failOnce(op, describe(cause));
      return undefined;
    }
  }

  async #done(op: Op): Promise<void> {
    await outboxStore.remove(op.opId);
    this.pending = this.pending.filter((entry) => entry.opId !== op.opId);
  }

  async #reject(op: Op, reason: string | undefined): Promise<void> {
    await outboxStore.remove(op.opId);
    this.pending = this.pending.filter((entry) => entry.opId !== op.opId);
    this.failed = [...this.failed, { ...op, lastError: reason ?? 'abgelehnt' }];
  }

  async #failOnce(op: Op, reason: string): Promise<void> {
    const attempts = op.attempts + 1;
    if (attempts >= MAX_ATTEMPTS) {
      await this.#reject(op, reason);
      return;
    }
    const updated = { ...op, attempts, lastError: reason };
    await outboxStore.put(updated);
    this.pending = this.pending.map((entry) => (entry.opId === op.opId ? updated : entry));
    this.#schedule(attempts);
  }

  #schedule(attempts = 1): void {
    if (this.#timer !== undefined) return;
    this.#timer = setTimeout(() => {
      this.#timer = undefined;
      void this.flush();
    }, backoffFor(attempts));
  }

  #refreshStatus(): void {
    if (this.failed.length > 0) {
      this.status = { kind: 'failed', failed: this.failed.length };
      return;
    }
    if (this.pending.length > 0) {
      this.status = { kind: 'waiting', pending: this.pending.length };
      return;
    }
    this.status = { kind: 'idle' };
  }
}

function describe(cause: unknown): string {
  if (cause instanceof ApiError) return cause.message;
  if (cause instanceof Error) return cause.message;
  return 'unbekannter Fehler';
}

export const outbox = new Outbox();
